import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { CheckCircle2, XCircle, Loader2, RefreshCw, ChevronDown, ChevronRight, Inbox, Upload, History, Undo2, AlertTriangle, PartyPopper, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/formatters';
import DailyReconciliationPanel from '../components/transactions/DailyReconciliationPanel';

// Plain YYYY-MM-DD → MM/DD/YYYY (no Date object: avoids the UTC-midnight day shift)
// 10/08 (Josh): the dashboard deep-links here — ?date=YYYY-MM-DD opens that day's tally, ?batch=<id> expands the batch.
const _LINK = new URLSearchParams(window.location.search);
const LINK_DATE = /^\d{4}-\d{2}-\d{2}$/.test(_LINK.get('date') || '') ? _LINK.get('date') : null;
const LINK_BATCH = _LINK.get('batch');

const fmtMDY = (d) => (d && d.length >= 10 ? `${d.slice(5, 7)}/${d.slice(8, 10)}/${d.slice(0, 4)}` : d || '');
const fmtMD = (d) => (d && d.length >= 10 ? `${d.slice(5, 7)}/${d.slice(8, 10)}` : d || '');

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STAGING = `${API}/daily-import/staging`;

// 10/08 (Josh): "after a file upload, if that day's payment renders the deal paid in
// full, I need some type of notification — maybe a dismissable banner". Approved
// payment batches carry result.paid_off_deals (backend utils/paid_off_detect). The
// banner lists them for the last PAID_OFF_WINDOW_DAYS until dismissed per batch
// (dismissals live in localStorage; an undone batch drops out on its own because
// its status is no longer 'approved').
const PAID_OFF_WINDOW_DAYS = 3;
const PAID_OFF_DISMISS_KEY = 'dailyImportReview.paidOffDismissed';
const readDismissed = () => {
  try { return new Set(JSON.parse(localStorage.getItem(PAID_OFF_DISMISS_KEY) || '[]')); } catch { return new Set(); }
};
const writeDismissed = (set) => {
  try { localStorage.setItem(PAID_OFF_DISMISS_KEY, JSON.stringify([...set].slice(-200))); } catch { /* storage unavailable: banner just reappears */ }
};
export const paidOffBannerRows = (approvedBatches, dismissed, now = new Date()) => {
  const cutoff = now.getTime() - PAID_OFF_WINDOW_DAYS * 86400000;
  const rows = [];
  for (const b of approvedBatches || []) {
    if (b.status !== 'approved' || b.kind !== 'payments' || dismissed.has(b.id)) continue;
    const when = Date.parse(b.reviewed_at || b.created_at || '');
    if (!Number.isFinite(when) || when < cutoff) continue;
    for (const d of (b.result?.paid_off_deals || [])) rows.push({ ...d, batch_id: b.id, filename: b.filename, reviewed_at: b.reviewed_at });
  }
  return rows;
};

export default function DailyImportReview() {
  const { getAuthHeaders, user } = useAuth();
  const isSuperAdmin = user?.roles?.includes('super_admin'); // 10/08 roles pass 3: Undo is super-only
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState(null);
  // 10/08 (Josh): the dashboard deep-links here — ?date=YYYY-MM-DD opens that day's tally, ?batch=<id> expands the batch.
  const linkApplied = useRef(false);
  const [detail, setDetail] = useState({});
  const [uploading, setUploading] = useState(false);
  const [dragKind, setDragKind] = useState(null);
  // References (ACH trace numbers) the reviewer chose to "upload anyway" despite being duplicates, keyed by batch id.
  const [forceRefs, setForceRefs] = useState({});
  // 'pending' = review queue, 'history' = all batches incl. approved/rejected
  const [view, setView] = useState('pending');
  // Approved batches with unknown-deal rows never re-staged (header bubble)
  const [unknowns, setUnknowns] = useState({ count: 0, batches: [] });
  // Deals rendered paid in full by recently approved payment batches (banner)
  const [paidOff, setPaidOff] = useState([]);
  const dismissPaidOff = (batchIds) => {
    const next = readDismissed();
    batchIds.forEach((id) => next.add(id));
    writeDismissed(next);
    setPaidOff((rows) => rows.filter((r) => !next.has(r.batch_id)));
  };
  const toggleForce = (batchId, ref) => setForceRefs((prev) => {
    const cur = new Set(prev[batchId] || []);
    if (cur.has(ref)) cur.delete(ref); else cur.add(ref);
    return { ...prev, [batchId]: [...cur] };
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const status = view === 'history' ? 'all' : 'pending';
      const [res, unk, appr] = await Promise.all([
        axios.get(`${STAGING}?status=${status}`, { headers: getAuthHeaders() }),
        // 08/20 (Josh): approved batches whose unknown rows "slipped through
        // the cracks" — drives the header bubble + Restage Unknowns button.
        axios.get(`${STAGING}/unknowns`, { headers: getAuthHeaders() }).catch(() => ({ data: { count: 0, batches: [] } })),
        // 10/08 (Josh): recently approved payment batches → "paid in full" banner.
        axios.get(`${STAGING}?status=approved`, { headers: getAuthHeaders() }).catch(() => ({ data: { batches: [] } })),
      ]);
      setBatches(res.data.batches || []);
      if (!linkApplied.current && (LINK_BATCH || LINK_DATE)) {
        linkApplied.current = true;
        if (LINK_BATCH && (res.data.batches || []).some((b) => b.id === LINK_BATCH)) setExpanded(LINK_BATCH);
        else if (LINK_BATCH) setView('history');
        setTimeout(() => document.querySelector(LINK_BATCH ? `[data-testid="batch-${LINK_BATCH}"]` : '[data-testid="daily-reconciliation-card"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
      }
      setUnknowns(unk.data || { count: 0, batches: [] });
      setPaidOff(paidOffBannerRows(appr.data?.batches || [], readDismissed()));
    } catch (e) {
      toast.error(`Failed to load review queue: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, view]);

  useEffect(() => { load(); }, [load]);

  // Upload a CSV straight to the staging endpoint (same path the bots push to).
  // Bounces go through the Bounce Bot adapter (Effective Entry Date, etc.), so
  // the raw bank file works here — and it still lands in this review queue.
  const uploadFiles = async (kind, fileList) => {
    const files = Array.from(fileList || []).filter((f) => f && f.name.toLowerCase().endsWith('.csv'));
    if (!files.length) {
      toast.error('Please drop .csv files only.');
      return;
    }
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${STAGING}/${kind}`, fd, { headers: { ...getAuthHeaders() } });
        if (res.data?.source_format === 'collector' && Array.isArray(res.data.batches)) {
          // One Collector sheet may stage a payments batch AND a bounces batch.
          const parts = res.data.batches.map((x) => `${x.new ?? 0} new ${x.kind}`).join(' + ');
          const errs = res.data.row_errors?.length ? ` · ${res.data.row_errors.length} row error(s) skipped` : '';
          toast.success(`${file.name}: Collector sheet → ${parts} staged for review${errs}`);
        } else {
          const n = res.data?.new ?? res.data?.summary?.new ?? 0;
          toast.success(`${file.name}: ${n} new ${kind} staged for review`);
        }
        ok++;
      } catch (e) {
        toast.error(`${file.name}: ${e.response?.data?.detail || e.message}`);
      }
    }
    setUploading(false);
    if (ok) await load();
  };

  const toggle = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    // Always refetch: pending batches are re-evaluated server-side against the
    // current deals, so the counts refresh after you create the missing deals.
    try {
      const res = await axios.get(`${STAGING}/${id}`, { headers: getAuthHeaders() });
      setDetail((d) => ({ ...d, [id]: res.data }));
      const s = res.data?.summary;
      if (s) {
        setBatches((bs) => bs.map((b) => b.id === id ? { ...b, counts: {
          total_rows: s.total_rows, new: s.new, duplicates: s.duplicates,
          unknown_deals: s.unknown_deals, fees_to_create: s.fees_to_create,
          new_amount: s.new_amount, fees_amount: s.fees_amount,
        } } : b));
      }
    } catch (e) {
      toast.error(`Failed to load batch: ${e.response?.data?.detail || e.message}`);
    }
  };

  const approve = async (id) => {
    if (!window.confirm('Approve and post this batch to deal balances? This cannot be auto-undone.')) return;
    setBusyId(id);
    try {
      const refs = forceRefs[id] || [];
      const qs = refs.length ? `?force_duplicate_refs=${encodeURIComponent(JSON.stringify(refs))}` : '';
      const res = await axios.post(`${STAGING}/${id}/approve${qs}`, {}, { headers: getAuthHeaders() });
      const r = res.data.result || {};
      toast.success(`Posted — ${r.payments_created ?? r.bounces_created ?? r.new ?? 0} new, ${r.fees_created ?? 0} fees`);
      if (Array.isArray(r.paid_off_deals) && r.paid_off_deals.length > 0) {
        toast.success(`Paid in full with this upload: ${r.paid_off_deals.map((d) => `#${d.deal_id_number}`).join(', ')}`, { duration: 10000 });
      }
      await load();
    } catch (e) {
      toast.error(`Approve failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  // 09/23 (Josh): a deploy landed while a batch was "posting…" and the commit was
  // killed mid-flight, leaving the batch stuck in 'committing'. The backend checks
  // what (if anything) landed and either returns it to pending or marks it failed.
  const releaseStuck = async (id) => {
    if (!window.confirm('This batch has been "posting" for over 10 minutes — the commit was probably interrupted. Release it? (Nothing is posted or deleted by this; the server checks what landed.)')) return;
    setBusyId(id);
    try {
      const res = await axios.post(`${STAGING}/${id}/release-stuck`, {}, { headers: getAuthHeaders() });
      toast.success(res.data?.message || `Batch is now ${res.data?.status}`);
      await load();
    } catch (e) {
      toast.error(`Release failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyId(null);
    }
  };
  const stuckFor = (b) => {
    const s = b?.commit?.started_at;
    if (!s) return null;
    return (Date.now() - new Date(s).getTime()) / 60000;
  };

  const reject = async (id) => {
    if (!window.confirm('Reject and discard this batch?')) return;
    setBusyId(id);
    try {
      await axios.post(`${STAGING}/${id}/reject`, {}, { headers: getAuthHeaders() });
      toast.success('Batch rejected');
      await load();
    } catch (e) {
      toast.error(`Reject failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  // 08/10 (Josh): a mis-click on Reject shouldn't be permanent — re-stage a
  // rejected batch as a fresh pending batch (original stays rejected for audit).
  const restageUnknowns = async () => {
    const list = unknowns.batches || [];
    if (!list.length) return;
    const ids = [...new Set(list.flatMap((b) => b.unknown_deal_ids || []))];
    const missedRows = list.reduce((a, b) => a + (b.missed_rows || 0), 0);
    const missedAmt = list.reduce((a, b) => a + (b.missed_amount || 0), 0);
    // 09/08 (Josh): "can the breakdown popup tell me which deals it is" —
    // aggregate the server-verified per-deal breakdown across batches.
    const dealAgg = {};
    for (const b of list) {
      for (const g of (b.missed_deals || [])) {
        const cur = dealAgg[g.deal_id] || { deal_id: g.deal_id, deal_name: g.deal_name || '', rows: 0, amount: 0 };
        cur.rows += g.rows || 0;
        cur.amount += g.amount || 0;
        if (!cur.deal_name && g.deal_name) cur.deal_name = g.deal_name;
        dealAgg[g.deal_id] = cur;
      }
    }
    const dealLines = Object.values(dealAgg).sort((a, b) => b.amount - a.amount);
    const dealText = dealLines.length
      ? '\n\nPosting to:\n' + dealLines.slice(0, 15).map(g =>
          ` • #${g.deal_id}${g.deal_name ? ` (${g.deal_name})` : ''} — ${g.rows} row(s), ${formatCurrency(g.amount)}`
        ).join('\n') +
        (dealLines.length > 15 ? `\n • …and ${dealLines.length - 15} more deal(s)` : '')
      : '';
    // Counts are re-verified against current data server-side: rows that have
    // since posted through a later feed are excluded.
    if (!window.confirm(
      `Re-stage ${list.length} approved batch(es) with verified missed rows?\n\n` +
      `${missedRows} row(s) / ${formatCurrency(missedAmt)} ready to post now` +
      (ids.length ? `; still no deal for: ${ids.join(', ')}` : '') +
      dealText +
      '\n\nAlready-posted rows are skipped as duplicates — only the missed rows will post once you approve.')) return;
    setBusyId('unknowns');
    try {
      for (const b of list) {
        await axios.post(`${STAGING}/${b.id}/restage`, {}, { headers: getAuthHeaders() });
      }
      toast.success(`${list.length} batch(es) re-staged — now pending review.`);
      await load();
    } catch (e) {
      toast.error(`Re-stage failed: ${e.response?.data?.detail || e.message}`);
      await load();
    } finally {
      setBusyId(null);
    }
  };

  // P2 (09/22/2026): Undo — preview modal, then POST /undo (server re-validates).
  const [undo, setUndo] = useState(null);          // { batch, preview, loading, running }
  const openUndo = async (b) => {
    setUndo({ batch: b, preview: null, loading: true, running: false });
    try {
      const res = await axios.get(`${API}/daily-import/batches/${b.id}/undo-preview`, { headers: getAuthHeaders() });
      setUndo({ batch: b, preview: res.data, loading: false, running: false });
    } catch (e) {
      toast.error(`Undo preview failed: ${e.response?.data?.detail || e.message}`);
      setUndo(null);
    }
  };
  const confirmUndo = async () => {
    if (!undo?.preview?.can_undo) return;
    const id = undo.batch.id;
    setUndo((u) => ({ ...u, running: true }));
    setBusyId(id);
    try {
      const res = await axios.post(`${API}/daily-import/batches/${id}/undo`, {}, { headers: getAuthHeaders() });
      const c = res.data?.counts || {};
      toast.success(`Undone — ${c.delete ?? 0} row(s) deleted, ${c.restore ?? 0} restored, ${c.deals ?? 0} deal(s) recalculated`);
      setUndo(null);
      await load();
    } catch (e) {
      toast.error(`Undo failed: ${e.response?.data?.detail || e.message}`);
      setUndo((u) => (u ? { ...u, running: false } : u));
    } finally {
      setBusyId(null);
    }
  };

  const restage = async (id) => {
    if (!window.confirm('Re-stage this batch as a new pending batch?')) return;
    setBusyId(id);
    try {
      const res = await axios.post(`${STAGING}/${id}/restage`, {}, { headers: getAuthHeaders() });
      const r = res.data || {};
      toast.success(`Re-staged — ${r.new ?? 0} new, ${r.duplicates ?? 0} dup. Now pending review.`);
      await load();
    } catch (e) {
      toast.error(`Re-stage failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Layout>
      <div className="p-6 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Daily Import Review</h1>
            <p className="text-sm text-gray-500">Payments &amp; bounces pushed by the bots — approve to post to the ledger.</p>
          </div>
          <div className="flex gap-2">
            <Button variant={view === 'history' ? 'default' : 'outline'}
              onClick={() => { setExpanded(null); setView((v) => (v === 'history' ? 'pending' : 'history')); }}
              disabled={loading || uploading}>
              <History className="w-4 h-4 mr-2" /> {view === 'history' ? 'Back to Pending' : 'History'}
            </Button>
            <Button variant="outline" onClick={load} disabled={loading || uploading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            {unknowns.count > 0 && (
              <Button variant="outline" onClick={restageUnknowns}
                disabled={loading || uploading || busyId === 'unknowns'}
                className="relative border-orange-300 text-orange-700 hover:bg-orange-50"
                title={`Verified missed rows (never posted): ${(unknowns.batches || []).reduce((a, b) => a + (b.missed_rows || 0), 0)} row(s)` +
                  ((unknowns.batches || []).some(b => (b.unknown_deal_ids || []).length)
                    ? ` · still no deal for: ${[...new Set((unknowns.batches || []).flatMap(b => b.unknown_deal_ids || []))].join(', ')}`
                    : '')}
                data-testid="restage-unknowns-btn">
                {busyId === 'unknowns'
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <RefreshCw className="w-4 h-4 mr-2" />} Restage Unknowns
                <span className="absolute -top-1.5 -right-1.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-orange-500 px-1 text-[11px] font-bold text-white">
                  {unknowns.count}
                </span>
              </Button>
            )}
          </div>
        </div>

        {paidOff.length > 0 && (
          <div className="mb-6 rounded-lg border border-green-300 bg-green-50 p-4" data-testid="paid-off-banner">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <PartyPopper className="w-5 h-5 text-green-700 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold text-green-900">
                    {paidOff.length === 1 ? 'A deal was paid in full by an approved upload' : `${paidOff.length} deals were paid in full by approved uploads`}
                  </div>
                  <ul className="mt-1 space-y-0.5 text-sm text-green-900">
                    {paidOff.map((d) => (
                      <li key={`${d.batch_id}-${d.deal_uuid}`} className="flex flex-wrap items-center gap-x-2">
                        <Link to={`/deals/${d.deal_uuid}`} className="font-medium underline">#{d.deal_id_number} {d.deal_name}</Link>
                        <span className="text-xs text-green-800">
                          {formatCurrency(d.balance_before)} → {formatCurrency(d.balance_after)}
                          {d.status_after ? ` · now ${d.status_after}` : ''}
                          {d.reviewed_at ? ` · posted ${fmtMDY(d.reviewed_at.slice(0, 10))}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="shrink-0 text-green-900"
                onClick={() => dismissPaidOff([...new Set(paidOff.map((d) => d.batch_id))])}
                title="Dismiss" data-testid="paid-off-banner-dismiss">
                <X className="w-4 h-4 mr-1" /> Dismiss
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {[
            { kind: 'bounces', label: 'Bounces', sub: 'ACH returns — raw Bounce Bot CSV or Collector sheet (Collector Name / Date / Merchant Number / Amount). Fees post ONLY from Fee rows in the file — no fee is derived from the return code.' },
            { kind: 'payments', label: 'Payments', sub: 'Payment Bot feed, MCASuite, Standard, or Collector sheet CSV(s). A Collector sheet with bounces is split into a payments batch and a bounces batch.' },
          ].map(({ kind, label, sub }) => (
            <div key={kind}
              onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragKind(kind); }}
              onDragLeave={() => setDragKind((k) => (k === kind ? null : k))}
              onDrop={(e) => { e.preventDefault(); setDragKind(null); uploadFiles(kind, e.dataTransfer.files); }}
              className={`rounded-lg border-2 border-dashed p-6 text-center transition-colors ${dragKind === kind ? 'border-blue-500 bg-blue-50' : 'border-gray-300'} ${uploading ? 'opacity-60 pointer-events-none' : ''}`}>
              {uploading ? <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-gray-400" /> : <Upload className="w-6 h-6 mx-auto mb-2 text-gray-400" />}
              <div className="font-medium">Drop {label} CSVs here</div>
              <div className="text-xs text-gray-500 mt-1">{sub}</div>
              <label className="mt-2 inline-block text-sm text-blue-600 underline cursor-pointer">
                or browse
                <input type="file" accept=".csv" multiple className="hidden"
                  onChange={(e) => { uploadFiles(kind, e.target.files); e.target.value = ''; }} />
              </label>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
          </div>
        ) : batches.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Inbox className="w-10 h-10 mb-3" />
              <p>{view === 'history' ? 'No batches yet.' : "No pending batches. You're all caught up."}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {batches.map((b) => {
              const c = b.counts || {};
              const d = detail[b.id];
              const rows = d?.summary?.preview_rows || [];
              return (
                <Card key={b.id} data-testid={`batch-${b.id}`}>
                  <CardHeader className="cursor-pointer" onClick={() => toggle(b.id)}>
                    <CardTitle className="flex items-center justify-between text-base">
                      <span className="flex items-center gap-2">
                        {expanded === b.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        <Badge variant={b.kind === 'payments' ? 'default' : 'destructive'}>{b.kind}</Badge>
                        <span className="font-mono text-xs text-gray-500">{b.filename}</span>
                        {/* 09/24 (Josh): the bot names files by RUN time — show the rows' actual
                            payment dates so a file can't be mistaken for the wrong day. */}
                        {b.payment_dates && (b.payment_dates.mixed ? (
                          <span className="flex items-center gap-1" title="A batch should contain a single date unless this was a date-range report.">
                            <Badge className="bg-amber-500 text-white">MIXED DATES</Badge>
                            <span className="text-xs text-amber-700">
                              {fmtMD(b.payment_dates.date_min)}–{fmtMD(b.payment_dates.date_max)} · {b.payment_dates.row_count} rows
                            </span>
                          </span>
                        ) : (
                          <span className="text-xs text-gray-600">
                            {b.kind === 'bounces' ? 'Returns dated' : 'Payments dated'} {fmtMDY(b.payment_dates.date_min)} · {b.payment_dates.row_count} rows
                          </span>
                        ))}
                      </span>
                      <span className="flex items-center gap-2 text-sm font-normal">
                        {/* 09/22 (Josh): a rejected batch is history only — nothing was posted, so no
                            new/$ badges. Approved rows show what actually POSTED (result), not the preview. */}
                        {b.status === 'rejected' ? (
                          <Badge variant="secondary">Rejected — nothing posted</Badge>
                        ) : b.status === 'committing' ? (<>
                          <Badge className="bg-blue-600"><Loader2 className="w-3 h-3 mr-1 animate-spin" />posting…</Badge>
                          {(stuckFor(b) ?? 0) >= 10 && (
                            <Button size="sm" variant="outline" className="h-6 text-xs"
                                    disabled={busyId === b.id}
                                    onClick={(e) => { e.stopPropagation(); releaseStuck(b.id); }}
                                    title={`Posting for ${Math.round(stuckFor(b))} min — the commit was probably interrupted`}>
                              Release stuck commit
                            </Button>
                          )}
                        </>) : b.status === 'commit_failed' ? (
                          <Badge variant="destructive" title={b.commit?.error || ''}>commit failed — check rows</Badge>
                        ) : b.status === 'undone' ? (
                          <Badge variant="secondary">Undone</Badge>
                        ) : b.status === 'approved' ? (<>
                          <Badge variant="default">approved</Badge>
                          <Badge variant="outline">{(b.result?.created ?? b.result?.bounces_created ?? c.new ?? 0)} posted</Badge>
                          {b.result?.fees_created ? <Badge variant="outline">{b.result.fees_created} fees</Badge> : null}
                        </>) : (<>
                          <Badge variant="outline">{c.new ?? 0} new</Badge>
                          {c.duplicates ? <Badge variant="secondary">{c.duplicates} dup</Badge> : null}
                          {c.unknown_deals ? <Badge variant="destructive">{c.unknown_deals} unknown</Badge> : null}
                        </>)}
                      </span>
                    </CardTitle>
                    <p className="text-xs text-gray-400 mt-1">
                      {b.created_at} · {b.created_by}
                      {b.status === 'approved' && b.result?.new_amount != null
                        ? <span className="ml-2 text-gray-500 font-medium">{formatCurrency(b.result.new_amount)} posted</span>
                        : (b.status !== 'rejected' && c.new_amount != null
                          ? <span className="ml-2 text-gray-500 font-medium">{formatCurrency(c.new_amount)}</span> : null)}
                      {b.reviewed_at ? <span className="ml-2">· {b.status} {b.reviewed_at} by {b.reviewed_by}</span> : null}
                    </p>
                  </CardHeader>
                  {expanded === b.id && (
                    <CardContent>
                      {/* 08/20 (Josh): "when there is an unknown in payments or
                          bounces, can it tell me that unknown. like 'no deal
                          names 10999 found'" */}
                      {(d?.summary?.unknown_deal_ids?.length > 0) && (
                        <div className="mb-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
                          data-testid="unknown-deals-banner">
                          <span className="font-semibold">
                            No deal found for: {d.summary.unknown_deal_ids.join(', ')}
                          </span>
                          <span className="block text-xs text-red-700 mt-0.5">
                            Create the deal or add a Deal ID Alias (Data Center), then re-open this batch — it re-checks automatically.
                          </span>
                        </div>
                      )}
                      {rows.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Deal ID</TableHead>
                              <TableHead>Deal Name</TableHead>
                              <TableHead>Date</TableHead>
                              <TableHead className="text-right">Amount</TableHead>
                              <TableHead>Reference</TableHead>
                              {b.kind === 'bounces' && <TableHead>Code</TableHead>}
                              {b.kind === 'bounces' && <TableHead className="text-right">Fee</TableHead>}
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {rows.map((r, i) => (
                              <TableRow key={i}>
                                <TableCell className="font-medium">{r.deal_id}</TableCell>
                                <TableCell className="text-gray-600">{r.deal_name}</TableCell>
                                <TableCell>{r.date}</TableCell>
                                <TableCell className="text-right">{formatCurrency(r.amount)}</TableCell>
                                <TableCell className="font-mono text-xs text-gray-500">{r.reference}</TableCell>
                                {b.kind === 'bounces' && (
                                  <TableCell>
                                    {r.kind === 'FEE' ? <Badge variant="outline">FEE ROW</Badge> : (r.return_code || '—')}
                                  </TableCell>
                                )}
                                {b.kind === 'bounces' && (
                                  <TableCell className="text-right">
                                    {r.kind === 'FEE' ? formatCurrency(r.amount)
                                      : r.fee_waived ? <span title="No fee row in the file for this return">—</span>
                                      : (
                                        <span className={r.fee_status === 'duplicate' ? 'line-through text-gray-400' : ''}
                                          title={r.fee_status === 'duplicate' ? 'Fee already posted under this trace — will be skipped' : 'Fee from the file'}>
                                          {formatCurrency(r.fee_amount)}
                                        </span>
                                      )}
                                  </TableCell>
                                )}
                                <TableCell>
                                  <Badge variant={r.status === 'pending' ? 'default' : r.status === 'duplicate' ? 'secondary' : 'destructive'}>{r.status}</Badge>
                                  {r.reason ? <span className="text-xs text-gray-400 ml-2">{r.reason}</span> : null}
                                  {r.warning ? <span className="text-xs text-amber-700 ml-2" data-testid="preview-warning">⚠ {r.warning}</span> : null}
                                  {b.kind === 'bounces' && r.kind === 'BOUNCE' && r.status === 'pending' && r.linked ? (
                                    <span className="text-xs text-emerald-700 ml-2" title={`Links to payment ${r.linked.id}`}>↩ links {r.linked.date}</span>
                                  ) : null}
                                  {r.status === 'duplicate' && r.reference ? (
                                    <label className="inline-flex items-center gap-1 ml-2 text-xs text-blue-600 cursor-pointer">
                                      <input type="checkbox" checked={(forceRefs[b.id] || []).includes(r.reference)} onChange={() => toggleForce(b.id, r.reference)} />
                                      Upload anyway
                                    </label>
                                  ) : null}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <p className="text-sm text-gray-400">Preview shows first rows only. {c.new ?? 0} new to post.</p>
                      )}
                      <div className="flex items-center justify-between gap-3 mt-4">
                        <div className="text-sm">
                          <span className="text-gray-500">{b.kind === 'bounces' ? 'Total reversed: ' : 'Total deposits: '}</span>
                          <span className="font-semibold">{formatCurrency(d?.summary?.new_amount ?? c.new_amount ?? 0)}</span>
                          {b.kind === 'bounces' && ((d?.summary?.fees_amount ?? c.fees_amount) ? (
                            <span className="text-gray-500 ml-3">Fees: <span className="font-semibold text-gray-700">{formatCurrency(d?.summary?.fees_amount ?? c.fees_amount)}</span></span>
                          ) : null)}
                        </div>
                        <div className="flex gap-3">
                          {(!b.status || b.status === 'pending') && (<>
                          <Button variant="outline" onClick={() => reject(b.id)} disabled={busyId === b.id}>
                            <XCircle className="w-4 h-4 mr-2" /> Reject
                          </Button>
                          <Button onClick={() => approve(b.id)} disabled={busyId === b.id}>
                            {busyId === b.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                            Approve &amp; Post ({c.new ?? 0})
                          </Button>
                          </>)}
                          {/* P2 (09/22/2026): Undo — committed batches only, immediately LEFT of Re-stage */}
                          {isSuperAdmin && (b.status === 'approved' || b.status === 'commit_failed' || b.status === 'undone') && (
                            <Button variant="outline" onClick={() => openUndo(b)} disabled={busyId === b.id || b.status === 'undone'}
                              title={b.status === 'undone' ? `Undone ${b.undone_at || ''} by ${b.undone_by || ''}` : 'Delete exactly what this upload posted'}
                              className={b.status === 'undone' ? '' : 'border-red-300 text-red-700 hover:bg-red-50'}
                              data-testid={`undo-${b.id}`}>
                              {busyId === b.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Undo2 className="w-4 h-4 mr-2" />}
                              {b.status === 'undone' ? 'Undone' : 'Undo'}
                            </Button>
                          )}
                          {(b.status === 'rejected' || b.status === 'undone' || b.status === 'commit_failed'
                            || (b.status === 'approved' && (b.counts?.unknown_deals || 0) > 0)) && !b.restaged_to && (
                            <Button variant="outline" onClick={() => restage(b.id)} disabled={busyId === b.id}
                              data-testid={`restage-${b.id}`}>
                              {busyId === b.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                              Re-stage
                            </Button>
                          )}
                          {(b.status === 'rejected' || b.status === 'undone') && b.restaged_to && (
                            <span className="text-xs text-gray-400 self-center">re-staged</span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* P2 — Undo confirmation modal */}
      <Dialog open={!!undo} onOpenChange={(o) => { if (!o && !undo?.running) setUndo(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="undo-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Undo2 className="w-5 h-5" /> Undo upload — {undo?.batch?.filename}
            </DialogTitle>
          </DialogHeader>
          {undo?.loading ? (
            <div className="flex items-center gap-2 py-6 text-gray-500"><Loader2 className="w-5 h-5 animate-spin" /> Working out exactly what this upload posted…</div>
          ) : undo?.preview ? (() => {
            const p = undo.preview;
            return (
              <div className="space-y-4 text-sm">
                <p className="text-gray-600">
                  Deletes <b>{p.counts?.delete ?? 0}</b> row(s) ({formatCurrency(p.counts?.delete_total ?? 0)}) this upload created,
                  restores <b>{p.counts?.restore ?? 0}</b> row(s) it modified, and recalculates <b>{p.counts?.deals ?? 0}</b> deal(s).
                  Not a void — the rows are removed as if never uploaded (a snapshot is archived).
                  {p.legacy ? <span className="ml-1 text-amber-700">Legacy batch: rows identified by deal + date + amount + type + reference + commit window.</span> : null}
                </p>
                {p.blockers?.length > 0 && (
                  <div className="rounded-md border border-red-300 bg-red-50 p-3 text-red-800" data-testid="undo-blockers">
                    <div className="font-semibold flex items-center gap-1"><XCircle className="w-4 h-4" /> Cannot undo</div>
                    <ul className="list-disc ml-5 mt-1">
                      {p.blockers.map((bl, i) => <li key={i}><span className="font-mono text-xs">{bl.code}</span> {bl.row || bl.detail}</li>)}
                    </ul>
                  </div>
                )}
                {p.warnings?.length > 0 && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900" data-testid="undo-warnings">
                    <div className="font-semibold flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Warnings (undo still allowed)</div>
                    <ul className="list-disc ml-5 mt-1">
                      {p.warnings.map((w, i) => <li key={i}>{w.detail}</li>)}
                    </ul>
                  </div>
                )}
                {p.deals?.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Deal</TableHead>
                        <TableHead className="text-right">Rows deleted</TableHead>
                        <TableHead className="text-right">$ deleted</TableHead>
                        <TableHead className="text-right">Restored</TableHead>
                        <TableHead className="text-right">Balance before</TableHead>
                        <TableHead className="text-right">Balance after</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {p.deals.map((d) => (
                        <TableRow key={d.deal_id}>
                          <TableCell><span className="font-medium">#{d.deal_number}</span> <span className="text-gray-500">{d.deal_name}</span>
                            <div className="text-xs text-gray-400">{(d.delete || []).map((r) => `${r.type} ${r.date} ${formatCurrency(r.amount)}`).join(' · ')}</div>
                          </TableCell>
                          <TableCell className="text-right">{d.delete?.length ?? 0}</TableCell>
                          <TableCell className="text-right">{formatCurrency(d.delete_total ?? 0)}</TableCell>
                          <TableCell className="text-right">{d.restore?.length ?? 0}</TableCell>
                          <TableCell className="text-right">{formatCurrency(d.balance_before)}</TableCell>
                          <TableCell className="text-right font-semibold">{formatCurrency(d.balance_after)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            );
          })() : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUndo(null)} disabled={undo?.running}>Cancel</Button>
            <Button variant="destructive" onClick={confirmUndo} disabled={undo?.loading || undo?.running || !undo?.preview?.can_undo}
              data-testid="undo-confirm">
              {undo?.running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Undo2 className="w-4 h-4 mr-2" />}
              Undo this upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Daily Reconciliation — relocated from the retired Data Center page (Josh 10/07).
          Read-only check of what posted on a given day; pick a date and load. */}
      <Card className="mt-6" data-testid="daily-reconciliation-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <RefreshCw className="h-4 w-4 text-slate-500" /> Daily Reconciliation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DailyReconciliationPanel defaultDate={LINK_DATE || new Date().toISOString().slice(0, 10)} autoLoad={!!LINK_DATE} />
        </CardContent>
      </Card>
    </Layout>
  );
}
